import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import isEqual from 'lodash/isEqual';
import { convertToApplicationError } from 'features/error-handling/errorUtils';
import { logListenerError } from 'features/firebase/services/listenerErrorLog';
import { listenForFirestoreDocumentSnapshots } from 'features/firebase/services/subscriptionService';
import {
  getActivityTaskDocumentPath,
  toClientActivityTask,
} from 'features/questionnaires/services/documentService';
import { selectReadyToReceiveSnapshots } from 'features/questionnaires/store/questionnaireSelectors';
import { errorOccurred } from 'features/questionnaires/store/questionnaireSlice';
import { syncQuestionnaireFromTaskSnapshot } from 'features/questionnaires/store/questionnaireSnapshotSync';
import { selectWindowId } from 'features/system-info/store/systemInfoSelectors';
import { isIntentionalNavigation } from 'shared/utils/intentionalNavigationGuard';
import {
  activityTaskNotFound,
  activityTaskSnapshotReceived,
} from './activityTaskSlice';
import type { DbTask } from '@hub/common';
import type { RootState } from 'features/store/store';

type Params = {
  userId: string;
  participationId: string;
  activityName: string;
};

// Listens to the active participation's task document. The snapshot stream gates
// the page (received / not-found / status via `activityTaskSlice`) and, once the
// questionnaire is initialized (`readyToReceiveSnapshots`), drives multi-window
// PROSE sync keyed by `windowId`.
export const activityTaskDocumentApi = createApi({
  reducerPath: 'activityTaskDocumentApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['activityTask'],
  endpoints: builder => ({
    activityTaskSubscription: builder.query<null, Params>({
      providesTags: ['activityTask'],
      keepUnusedDataFor: 0,
      queryFn() {
        return { data: null };
      },
      async onCacheEntryAdded(
        params,
        // eslint-disable-next-line @typescript-eslint/unbound-method -- destructured RTK Query lifecycle helpers, per Redux docs
        { dispatch, getState, cacheEntryRemoved }
      ) {
        const documentPath = getActivityTaskDocumentPath(
          params.userId,
          params.participationId,
          params.activityName
        );

        // A write carrying a server timestamp emits twice: the local write, then
        // the server acknowledgement, which differs only in lastActivityAt.
        let previous: DbTask | undefined;

        const onSnapshot = (rawData: DbTask) => {
          const data = toClientActivityTask(rawData);
          if (previous !== undefined && isEqual(previous, data)) {
            return;
          }
          previous = data;

          // Gate: record the snapshot for the page lifecycle on every update.
          dispatch(
            activityTaskSnapshotReceived({
              participationId: params.participationId,
              task: data,
            })
          );

          // Multi-window sync: only once the questionnaire is initialized.
          const state = getState() as RootState;
          if (selectReadyToReceiveSnapshots(state)) {
            syncQuestionnaireFromTaskSnapshot(
              dispatch,
              data,
              selectWindowId(state)
            );
          }
        };

        const onError = (error: Error) => {
          const quiet = logListenerError(
            'Error in activity task snapshot listener',
            error
          );

          if (
            selectReadyToReceiveSnapshots(getState() as RootState) &&
            !isIntentionalNavigation() &&
            !quiet
          ) {
            dispatch(
              errorOccurred(convertToApplicationError(error).toErrorData())
            );
          }
        };

        const onSnapshotEmpty = () => {
          previous = undefined;
          dispatch(
            activityTaskNotFound({
              participationId: params.participationId,
              activityName: params.activityName,
            })
          );
        };

        await listenForFirestoreDocumentSnapshots(
          cacheEntryRemoved,
          documentPath,
          onSnapshot,
          onError,
          onSnapshotEmpty
        );
      },
    }),
  }),
});

export const { useActivityTaskSubscriptionQuery } = activityTaskDocumentApi;
